import React from "react";
import { PlainChunk } from "./PlainChunk.jsx";
import { ListChunk } from "./ListChunk.jsx";
import { ArticleList } from "./ArticleList.jsx";
import { markdown } from "markdown";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  useGetUserArticles,
  useUpdateUser,
  useGetUser,
  useGetSubscriptions,
  useUpdateSubscription,
} from "../utils/api.js";

const queryClient = new QueryClient();

export const App = ({ profileUser, editable = false }) => {
  return (
    <QueryClientProvider client={queryClient}>
      <ProfilePage profileUser={profileUser} editable={editable} />
    </QueryClientProvider>
  );
};

export const ProfilePage = ({ profileUser, editable = false }) => {
  const { isSuccess: isUserSuccess, data: user } = useGetUser(profileUser);
  const updateUser = useUpdateUser(profileUser);

  const { isSuccess: isArticleSuccess, data: articles } = useGetUserArticles(
    user?.id,
  );

  const { isSuccess: isSubscriptionsSuccess, data: subscriptions } =
    useGetSubscriptions(user?.id);

  const updateSubscription = useUpdateSubscription(user?.id);

  const isLoading = !isUserSuccess || !isArticleSuccess;

  // Show/hide loading overlay and swap content when data is loaded
  React.useEffect(() => {
    const loadingOverlay = document.getElementById('profile-loading-overlay');
    
    if (isUserSuccess && isArticleSuccess) {
      // Hide loading overlay
      if (loadingOverlay) {
        loadingOverlay.style.display = 'none';
      }
      
      // Hide static content and show React content
      const staticContent = document.getElementById('profile-static-content');
      const reactContent = document.getElementById('profile-react-content');
      
      if (staticContent) {
        staticContent.style.display = 'none';
      }
      if (reactContent) {
        reactContent.style.display = 'block';
      }
    } else {
      // Show loading overlay
      if (loadingOverlay) {
        loadingOverlay.style.display = 'flex';
      }
    }
  }, [isUserSuccess, isArticleSuccess]);

  if (isLoading) {
    // Render loading overlay and return null for content
    return (
      <>
        <div id="profile-loading-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 9999,
          backdropFilter: 'blur(4px)',
          backgroundColor: 'rgba(255, 255, 255, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column'
        }}>
          <div style={{
            background: 'white url("/assets/piston_throbber.gif") center no-repeat',
            width: '180px',
            height: '180px',
            borderRadius: '180px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            marginBottom: '20px'
          }} />
          <div style={{
            fontSize: '18px',
            fontWeight: 'bold',
            color: '#333',
            textShadow: '0 1px 2px rgba(255, 255, 255, 0.8)',
            fontFamily: 'League Gothic, LeagueGothicRegular, Helvetica, Arial, sans-serif',
            letterSpacing: '0.5px'
          }}>
            Loading Editor's Profile
          </div>
        </div>
      </>
    );
  }

  const editableFields = (
    <span>
      <p>
        Change your avatar on{" "}
        <a
          href="https://gravatar.com"
          title="Change your Avatar"
          target="_blank"
        >
          Gravatar
        </a>
      </p>
      <PlainChunk
        field="location"
        model={user}
        onChange={updateUser.mutate}
        singleLine
        label="Location"
        help="Enter your city and or state"
      />
      <ListChunk
        field="garage"
        model={user}
        onChange={updateUser.mutate}
        label="In My Garage"
        supportsMarkdown
      />
      <PlainChunk
        field="bio"
        model={user}
        onChange={updateUser.mutate}
        label="About Me"
        supportsMarkdown
        help="Say a little about yourself"
      />
      <PlainChunk
        field="forum"
        model={user}
        onChange={updateUser.mutate}
        singleLine
        label="Find me here"
        help="Url to your favorite car forum"
      />
      <PlainChunk
        field="amazon_affiliate"
        model={user}
        onChange={updateUser.mutate}
        singleLine
        label="Amazon Affiliate ID"
        help="Enter your associates tag. EX: 'howtune-20' **Leave blank to support HowTune**"
      />

      <section id="social">
        <h1 className="label">Enable Facebook Login:</h1>
        <div className="buttons">
          <div className="social">
            <a className="facebook-login button" href="/auth/facebook">
              Enable Facebook Login
            </a>
          </div>
        </div>
      </section>

      <section id="social">
        <h1 className="label">Make Money:</h1>
        <div className="text">
          To get started earning ad revenue on your articles{" "}
          <a href="mailto:support@howtune.com">send us an email</a> with the
          following:
          <ul>
            <li>
              Your Howtune <em>username</em>
            </li>
            <li>
              Your <em>Google Adsense ID</em>
            </li>
            <li>A little background about you and your cars</li>
          </ul>
        </div>
      </section>

      <section className="profile-notifications">
        <h1>Comment Notifications</h1>
        <ul>
          {subscriptions.map(
            function (s, i) {
              return (
                <li key={"notification-" + i}>
                  <label>
                    <input
                      type="checkbox"
                      value={s.article_id}
                      checked={s.enabled}
                      onChange={() => updateSubscription.mutate(s)}
                    />
                    {s.article_name} <span>({s.vehicle})</span>
                  </label>
                </li>
              );
            }.bind(this),
          )}
        </ul>
      </section>
    </span>
  );

  const staticFields = (
    <span>
      <section id="location">
        <h1>Location:</h1>
        <span>{user.location}</span>
      </section>

      <section id="garage">
        <h1>In My Garage:</h1>
        <span
          dangerouslySetInnerHTML={{
            __html: markdown.toHTML(user.garage),
          }}
        />
      </section>

      <section id="profile-bio">
        <h1 className="label">About Me:</h1>
        <span
          dangerouslySetInnerHTML={{
            __html: markdown.toHTML(user.bio),
          }}
        />
      </section>
    </span>
  );

  return (
    <section className="profile" id="profile-react-content" style={{ display: 'none' }}>
      <div id="column-left">
        <div className="profile-wrapper">
          <section className="author byline">
            <div className="profile-pic">
              <img src={user.avatar} />
            </div>
            <h1 className="profile-name">
              <span className="hidden">Written by:</span>
              {user.username}
            </h1>
            <div className="article-count">
              {"Joined " + user.joined_ago + " ago"}
            </div>
            <div className="article-count">
              <b>{user.publish_count}</b> articles
            </div>
            <div className="article-count">
              <b>{user.draft_count}</b> drafts
            </div>
          </section>
          {editable ? editableFields : staticFields}
        </div>
      </div>

      <div id="column-right">
        <section id="profile-articles" className="wide-cards">
          <h1>{user.username}'s Articles!</h1>

          <div className="new-article">
            <a className="button submit" href="/write">
              Start a New Article
            </a>
          </div>

          <ArticleList articles={articles} editable={editable} />
        </section>
      </div>
    </section>
  );
};
